<template>
    <aside 
        ref="outerElementRef" 
        class="aside-navigation"
        @focusin="onFocusInNav"
        @focusout="onFocusOutNav"
    >
        <div class="aside-navigation__title">
            {{ title }}
        </div>
        <nav class="aside-navigation__nav">
            <nuxt-link
                v-for="(item, idx) of props.routes"
                :id="`aside-navigation__nav-item-${idx}`"
                :key="item.code"
                :ref="setItemRef"
                :to="item.routerPath"
                event=""
                :class="{
                    'aside-navigation__nav-item': true,
                    'aside-navigation__nav-item_selected': getIsFocused(idx)
                }"
                @click="goToNextPage(item.routerPath, idx)"
            >
                <span class="aside-navigation__nav-item-text">
                    {{ $t(item.title) }}
                </span>
            </nuxt-link>
        </nav>
    </aside>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, onMounted, onBeforeUnmount, watch, type PropType, onBeforeUpdate  } from "vue";
    import { useRoute, useRouter } from 'vue-router';
    import { LinkItem } from "@/components/Header/types/LinkItem";
    import { useApplicationStore } from "@/stores/Application";
    import { useFocus } from "@/composables/useFocus";
    import { useFocusedComponents } from '@/composables/useFocusedComponents';
    
    const ApplicationStore = useApplicationStore();
    const { hideHeader } = ApplicationStore;

    const { focusToHeader } = useFocus();

    const route = useRoute();
    const router = useRouter();
    const props = defineProps({
        routes: {
            type: Array as PropType<LinkItem[]>,
            default: () => [],
            validator(value) {
                return (
                    Array.isArray(value) &&
                    !!value.filter((v) => v instanceof LinkItem)?.length
                );
            }
        },
        title: {
            type: String,
            default: ""
        },
        defaultRoute: {
            type: String,
            required: true
        }
    });
    const {  $eventBus, $navigator } = useNuxtApp();
    const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('AsideNavigation');
    const currentIndex: Ref<number> = ref(-1);
    let linksRef: Array<Element | ComponentPublicInstance | null> = [];
    const countslinks = computed((): number => props.routes.length)
    const prevIndex = computed((): number => {
        if (currentIndex.value < 0) {
            return -1;
        }
        return currentIndex.value - 1;
    });
    const nextIndex = computed((): number => {
        if (currentIndex.value === countslinks.value - 1) {
            return countslinks.value - 1;
        }
        return currentIndex.value + 1;
    });

    const setItemRef = (el: Element | ComponentPublicInstance | null) => {
        if (el) {
            linksRef.push(el)
        }
    }

    const goToNextPage = (link: string, index: number): void => {
        router.push(link);
        currentIndex.value = index;
    };
    const openSubpage = (): void => {
        const subPageCode: number | null  = route.query.openPage ? Number(route.query.openPage) : null;

        if (subPageCode === null) {
            return;
        }

        const findRoute: number = props.routes.findIndex((item) => item.code === subPageCode);
        if (findRoute !== -1) {
            goToNextPage(props.routes[findRoute].routerPath, findRoute);
        };
    };
    const navigate = (index: number): void => {
        if (!linksRef.length) {
            return;
        }
        const targetLink = (linksRef as ComponentPublicInstance[])[index]?.$el

        if (!(targetLink instanceof HTMLAnchorElement)) {
            navigate(0); // focusing on the first element of the list when disabling the dev mode and removing it from the list routes
            return;
        }

        targetLink.focus();
        currentIndex.value = index;
    };
    const focusToNavigation = (): void => {
        hideHeader();

        if (currentIndex.value < 0) {
            currentIndex.value = 0;
        }

        navigate(currentIndex.value);
    };
    const navigateUp = (): void => {
        if (currentIndex.value === 0) {
            focusToHeader();
        } else {
            navigate(prevIndex.value);
        }
    };
    const navigateDown = (): void => {
        navigate(nextIndex.value);
    };
    const onKeyBack = (): void => {
        focusToHeader();
    };
    const onKeyRight = (): void => {
        if (!linksRef.length) {
            return;
        }

        const targetLink = (linksRef as ComponentPublicInstance[])[currentIndex.value].$el;

        if (!(targetLink instanceof HTMLAnchorElement)) {
            return;
        }
        targetLink.click();
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:up", navigateUp);
        $navigator.on("keypress:down", navigateDown);
        $navigator.on("keypress:right", onKeyRight);
        $navigator.on("keypress:back", onKeyBack);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:up", navigateUp);
        $navigator.off("keypress:down", navigateDown);
        $navigator.off("keypress:right", onKeyRight);
        $navigator.off("keypress:back", onKeyBack);
    };
    const onFocusInNav = (): void => {
        registerKeyListeners();
        setFocused();
    };
    const onFocusOutNav = (): void => {
        unregisterKeyListeners();
        unsetFocused();
    };
    const getIsFocused = (idx: number): boolean => {
        return idx === currentIndex.value;
    };
    onBeforeUpdate(() => {
        linksRef = []
    });
    onMounted(() => {
        focusToHeader();
        $eventBus.on("leave-header", focusToNavigation);
        openSubpage();
    });
    onBeforeUnmount(() => {
        $eventBus.off("leave-header", focusToNavigation);
    });
    watch(route, (to) => {
        if (to.path === props.defaultRoute) {
            focusToNavigation();
        }
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>